import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { H1, Text } from '../components/ui/Typography';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';
import { calculateTax, getInsights } from '../utils/taxCalculator';
import { generateReportPDF } from '../utils/pdfGenerator';

const InheritanceTaxAnalysis = () => {
  const { lang = 'nl' } = useParams();
  const isEn = lang === 'en';
  
  const [formData, setFormData] = useState({
    estateValue: 250000,
    relation: isEn ? 'Partner' : 'Partner',
    numChildren: 2,
    testament: isEn ? 'No' : 'Nee',
    international: isEn ? 'No' : 'Nee',
    business: isEn ? 'No' : 'Nee',
    blended: isEn ? 'No' : 'Nee',
    age: '40–60'
  });

  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleAnalyze = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      const result = calculateTax(formData);
      const rawInsights = getInsights(formData);
      
      const potentialSavingMin = formData.estateValue * 0.08;
      const potentialSavingMax = formData.estateValue * 0.18;

      const insights = rawInsights.map((ins, idx) => ({
        ...ins,
        priority: ins.type === 'critical' ? (isEn ? 'HIGH' : 'HOOG') : ins.type === 'warning' ? (isEn ? 'MEDIUM' : 'MIDDEN') : (isEn ? 'LOW' : 'LAAG')
      }));

      setAnalysis({
        ...result,
        insights,
        savings: { min: potentialSavingMin, max: potentialSavingMax }
      });
      setIsAnalyzing(false);
    }, 1200);
  };

  const handleDownloadPDF = async () => {
    if (!analysis) return;
    await generateReportPDF({
      title: isEn ? 'Inheritance Tax Analysis' : 'Erfbelasting Analyse',
      date: new Date().toLocaleDateString(isEn ? 'en-US' : 'nl-NL'),
      analysis: {
        score: formData.estateValue > 1000000 ? '9.1' : formData.estateValue > 250000 ? '6.8' : '3.4',
        label: analysis.tax > 50000 ? (isEn ? 'High' : 'Hoog') : (isEn ? 'Average' : 'Gemiddeld'),
        protection: (formData.testament === 'Yes' || formData.testament === 'Ja') ? '8.5' : '3.0'
      },
      risks: analysis.insights.map(i => ({
        title: i.title,
        desc: i.desc,
        priority: i.priority,
        icon: i.title.includes('Business') || i.title.includes('Bedrijf') ? 'Briefcase' : i.title.includes('International') || i.title.includes('Internationaal') ? 'Globe' : 'Shield'
      })),
      steps: isEn ? [
        { step: 'Fiscal Restructuring', desc: 'Estimate your savings by using two-stage clauses or filling legacies.' },
        { step: 'Update Will', desc: 'Ensure your will aligns with current tax exemptions.' },
        { step: 'Gifting Plan', desc: 'Reduce future inheritance tax by utilizing annual gifting exemptions now.' }
      ] : [
        { step: 'Fiscale Herstructurering', desc: 'Schat uw besparing door gebruik te maken van tweetrapsclausules of opvullegaat.' },
        { step: 'Actualisatie Testament', desc: 'Zorg dat uw testament aansluit op de huidige fiscale vrijstellingen.' },
        { step: 'Schenkingsplan', desc: 'Verlaag de toekomstige erfbelasting door nu gebruik te maken van de jaarlijkse schenkingsvrijstelling.' }
      ],
      type: 'tax',
      savings: analysis.savings
    });
  };

  const relations = isEn 
    ? ['Partner', 'Child', 'Grandchild', 'Other']
    : ['Partner', 'Kind', 'Kleinkind', 'Overig'];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 md:py-20">
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-navy-50 text-navy-600 text-[10px] font-bold uppercase tracking-widest mb-6">
          <SafeIcon name="Calculator" /> {isEn ? 'Interactive Tool' : 'Interactieve Tool'}
        </div>
        <H1 className="text-gradient mb-4">{isEn ? 'Inheritance Tax Analysis' : 'Erfbelasting Analyse'}</H1>
        <Text className="max-w-2xl mx-auto text-slate-500">
          {isEn 
            ? 'Get immediate insight into possible inheritance tax, fiscal considerations, and risks for your situation.'
            : 'Krijg direct inzicht in mogelijke erfbelasting, fiscale aandachtspunten en risico’s voor jouw situatie.'}
        </Text>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-6">
          {isEn ? 'Strategic analysis. No legal jargon.' : 'Strategische analyse. Geen juridisch jargon.'}
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-5">
          <Card className="p-8 md:p-10 border-slate-100 shadow-2xl" hover={false}>
            <h3 className="text-xl font-bold text-navy-900 mb-8 flex items-center gap-3">
              <SafeIcon name="Settings" className="text-slate-400" /> {isEn ? 'Your Situation' : 'Jouw Situatie'}
            </h3>
            <div className="space-y-8">
              <div>
                <div className="flex justify-between mb-4">
                  <label className="text-sm font-bold text-navy-900 uppercase tracking-wider">{isEn ? 'Estate' : 'Nalatenschap'}</label>
                  <span className="text-navy-900 font-bold">€ {formData.estateValue.toLocaleString(isEn ? 'en-US' : 'nl-NL')}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="5000000"
                  step="10000"
                  value={formData.estateValue}
                  onChange={(e) => setFormData({ ...formData, estateValue: parseInt(e.target.value) })}
                  className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-navy-900"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 block">{isEn ? 'Relation to deceased' : 'Relatie tot overledene'}</label>
                <div className="grid grid-cols-2 gap-2">
                  {relations.map(rel => (
                    <button
                      key={rel}
                      onClick={() => setFormData({ ...formData, relation: rel })}
                      className={`py-3 px-4 rounded-xl text-sm font-semibold transition-all border ${formData.relation === rel ? 'bg-navy-900 text-white border-navy-900 shadow-lg' : 'bg-white text-slate-600 border-slate-100 hover:border-slate-300'}`}
                    >
                      {rel}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {[
                  { id: 'testament', label: isEn ? 'Have a will?' : 'Heeft u een testament?', icon: 'FileText' },
                  { id: 'international', label: isEn ? 'International?' : 'Internationaal?', icon: 'Globe' },
                  { id: 'business', label: isEn ? 'Own business?' : 'Eigen onderneming?', icon: 'Briefcase' },
                  { id: 'blended', label: isEn ? 'Blended family?' : 'Samengesteld gezin?', icon: 'Users' }
                ].map(item => (
                  <div key={item.id}>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block">{item.label}</label>
                    <div className="flex bg-slate-50 p-1 rounded-lg border border-slate-100">
                      {[isEn ? 'Yes' : 'Ja', isEn ? 'No' : 'Nee'].map(val => (
                        <button
                          key={val}
                          onClick={() => setFormData({ ...formData, [item.id]: val })}
                          className={`flex-1 py-1.5 text-[10px] font-bold rounded-md transition-all ${formData[item.id] === val ? 'bg-white text-navy-900 shadow-sm' : 'text-slate-400'}`}
                        >
                          {val}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <Button onClick={handleAnalyze} className="w-full py-5 shadow-xl shadow-navy-900/10 mt-4" disabled={isAnalyzing}>
                {isAnalyzing ? (isEn ? 'Intelligent Analysis...' : 'Intelligent Analysis...') : (isEn ? 'Analyze Situation' : 'Analyseer Situatie')}
              </Button>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            {!analysis ? (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full">
                <Card className="h-full min-h-[500px] border-dashed border-2 border-slate-200 bg-slate-50/30 flex flex-col items-center justify-center text-center p-12" hover={false}>
                  <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center text-slate-300 mb-8 shadow-sm">
                    <SafeIcon name="Cpu" className="text-3xl" />
                  </div>
                  <h3 className="text-xl font-bold text-navy-900 mb-3">{isEn ? 'No analysis available yet' : 'Nog geen analyse beschikbaar'}</h3>
                  <p className="text-slate-400 max-w-xs text-sm leading-relaxed">
                    {isEn 
                      ? 'Enter your details on the left for a detailed fiscal simulation of your inheritance.'
                      : 'Vul links jouw gegevens in voor een gedetailleerde fiscale simulatie van uw erfenis.'}
                  </p>
                </Card>
              </motion.div>
            ) : (
              <motion.div key="analysis" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <Card className="p-10 border-none bg-navy-950 text-white shadow-2xl relative overflow-hidden" hover={false}>
                  <div className="absolute inset-0 bg-blueprint-pattern opacity-[0.03]"></div>
                  <div className="relative z-10">
                    <div className="flex justify-between items-start mb-12">
                      <div>
                        <div className="text-[10px] font-bold text-blue-400 uppercase tracking-widest mb-2">{isEn ? 'Estimated Inheritance Tax' : 'Geschatte Erfbelasting'}</div>
                        <div className="text-4xl md:text-5xl font-bold tracking-tight">
                          € {Math.round(analysis.range.min).toLocaleString(isEn ? 'en-US' : 'nl-NL')} – {Math.round(analysis.range.max).toLocaleString(isEn ? 'en-US' : 'nl-NL')}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">{isEn ? 'Risk Profile' : 'Risicoprofiel'}</div>
                        <div className={`text-xl font-bold ${analysis.tax > 50000 ? 'text-amber-400' : 'text-emerald-400'}`}>
                          {analysis.tax > 50000 ? (isEn ? 'Elevated' : 'Verhoogd') : (isEn ? 'Limited' : 'Beperkt')}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-8 py-8 border-t border-white/10">
                      <div>
                        <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">{isEn ? 'Complexity' : 'Complexiteit'}</div>
                        <div className="text-lg font-bold">{formData.estateValue > 1000000 ? (isEn ? 'High' : 'Hoog') : (isEn ? 'Average' : 'Gemiddeld')}</div>
                      </div>
                      <div>
                        <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">{isEn ? 'Exemption' : 'Vrijstelling'}</div>
                        <div className="text-lg font-bold">€ {analysis.exemption.toLocaleString(isEn ? 'en-US' : 'nl-NL')}</div>
                      </div>
                      <div>
                        <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">{isEn ? 'Savings' : 'Besparing'}</div>
                        <div className="text-lg font-bold text-blue-400">€ {Math.round(analysis.savings.min / 1000)}k+</div>
                      </div>
                    </div>
                  </div>
                </Card>

                <div className="grid sm:grid-cols-2 gap-4">
                  {analysis.insights.map((insight, i) => (
                    <Card key={i} className="p-6 border-slate-100 bg-white" hover={true}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${insight.type === 'critical' ? 'bg-red-50 text-red-500' : insight.type === 'warning' ? 'bg-amber-50 text-amber-500' : 'bg-blue-50 text-blue-500'}`}>
                            <SafeIcon name={insight.type === 'critical' ? 'AlertTriangle' : 'Info'} />
                          </div>
                          <h4 className="font-bold text-navy-900 text-sm">{insight.title}</h4>
                        </div>
                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{insight.priority}</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">{insight.desc}</p>
                    </Card>
                  ))}
                </div>

                <div className="pt-4">
                  <Button variant="navy" className="w-full py-4 shadow-xl" onClick={handleDownloadPDF}>
                    <SafeIcon name="FileText" className="mr-2" /> {isEn ? 'Generate PDF Dossier' : 'Genereer PDF Dossier'}
                  </Button>
                  <p className="text-center text-[10px] text-slate-400 mt-4 px-12 leading-relaxed">
                    {isEn 
                      ? 'This analysis is a strategic indication in preparation for legal advice.'
                      : 'Deze analyse is een strategische indicatie ter voorbereiding op juridisch advies.'}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default InheritanceTaxAnalysis;